import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  fetchWarehouseTransfersPage,
  type WarehouseListPage,
  type WarehouseTransferRow,
  type WarehouseTransfersParams,
} from '@/data/warehouse';
import { describeStatus, formatCount } from '@/domain/warehouse';
import ErrorBanner from '../../../components/ErrorBanner';
import WindowCalendarView, {
  WindowCalendarLegend,
  type WindowCalendarItem,
  type WindowCalendarLegendItem,
} from '../../../components/calendar/WindowCalendarView';
import { readableErrorMessage } from '../../../utils/appErrors';
import { warehouseRecordHref } from './warehouseTabs';

/** At most CALENDAR_PAGE × CALENDAR_PAGES transfers are drawn in one window. */
const CALENDAR_PAGE = 100;
const CALENDAR_PAGES = 3;
/** Transfers that ship this many days before the visible range can still arrive inside it. */
const LEAD_DAYS = 14;

const LEGEND_ITEMS: readonly WindowCalendarLegendItem[] = [
  { tone: 'neutral', label: 'Draft' },
  { tone: 'info', label: 'Confirmed' },
  { tone: 'progress', label: 'Picking, shipped or in transit' },
  { tone: 'warn', label: 'Partially received' },
  { tone: 'ok', label: 'Received' },
  { tone: 'stopped', label: 'Cancelled' },
];

type TransfersCalendarViewProps = {
  /** The list's filters; the calendar replaces paging, grouping and the ship-date range. */
  params: WarehouseTransfersParams;
};

function later(left: string | null, right: string): string {
  return left && left > right ? left : right;
}

function earlier(left: string | null, right: string): string {
  return left && left < right ? left : right;
}

async function fetchCalendarRows(params: WarehouseTransfersParams): Promise<{ rows: WarehouseTransferRow[]; total: number }> {
  const rows: WarehouseTransferRow[] = [];
  let total = 0;
  for (let page = 0; page < CALENDAR_PAGES; page += 1) {
    const result: WarehouseListPage<WarehouseTransferRow> = await fetchWarehouseTransfersPage({
      ...params,
      offset: page * CALENDAR_PAGE,
      limit: CALENDAR_PAGE,
    });
    rows.push(...result.rows);
    total = result.total;
    if (rows.length >= total || result.rows.length < CALENDAR_PAGE) break;
  }
  return { rows, total };
}

/**
 * Transfers as bars from ship day to arrival, with the expected return as a whisker. Reads the
 * same server list as the table, so every toolbar filter applies; the visible range sets the
 * ship-date window.
 */
export default function TransfersCalendarView({ params }: TransfersCalendarViewProps) {
  const [range, setRange] = React.useState<{ start: string; end: string } | null>(null);
  const handleRange = React.useCallback((start: string, end: string) => {
    setRange((current) => (current?.start === start && current?.end === end ? current : { start, end }));
  }, []);

  const windowParams = React.useMemo<WarehouseTransfersParams | null>(() => {
    if (!range) return null;
    const from = dayjs(range.start).subtract(LEAD_DAYS, 'day').format('YYYY-MM-DD');
    return {
      ...params,
      groupBy: 'off',
      sortKey: 'ship',
      sortDir: 'asc',
      offset: 0,
      limit: CALENDAR_PAGE,
      shipFrom: later(params.shipFrom, from),
      shipTo: earlier(params.shipTo, range.end),
    };
  }, [params, range]);

  const query = useQuery({
    queryKey: ['warehouse', 'transfers-calendar', windowParams],
    queryFn: () => (windowParams ? fetchCalendarRows(windowParams) : Promise.resolve({ rows: [], total: 0 })),
    enabled: windowParams != null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const items = React.useMemo<WindowCalendarItem<WarehouseTransferRow>[]>(() => (query.data?.rows ?? [])
    .filter((row) => row.shipDay)
    .map((row) => {
      const status = describeStatus('transfer', row.status);
      const route = `${row.fromSite?.name ?? 'Source'} to ${row.toSite?.name ?? 'Destination'}`;
      return {
        id: row.id,
        code: row.docNo ?? 'Transfer',
        title: row.description || route,
        subtitle: route,
        start: row.shipDay as string,
        end: row.arriveDay ?? row.shipDay,
        returnAt: row.scope === 'temporary' ? row.returnDueDay : null,
        href: warehouseRecordHref.transfer(row.id),
        tone: status.tone,
        statusLabel: status.label,
        data: row,
      };
    }), [query.data?.rows]);

  const shown = query.data?.rows.length ?? 0;
  const total = query.data?.total ?? 0;
  const countLabel = query.isLoading
    ? 'Loading…'
    : total > shown
      ? `(first ${formatCount(shown)} of ${formatCount(total)} transfers; narrow the filters to see the rest)`
      : `(${formatCount(total)} ${total === 1 ? 'transfer' : 'transfers'})`;

  return (
    <>
      {query.isError ? (
        <ErrorBanner
          message={readableErrorMessage(query.error, 'Transfers could not be loaded.')}
          onRetry={() => { void query.refetch(); }}
        />
      ) : null}
      <WindowCalendarView
        items={items}
        onDateRangeChange={handleRange}
        countLabel={countLabel}
        modeAriaLabel="Transfer calendar mode"
        returnWhiskerToggle={{ label: 'Returns', title: 'Show when temporary transfers are due back' }}
        emptyDayLabel="No transfers on this day."
        openLabel={(item) => `Open transfer ${item.code}`}
        legend={<WindowCalendarLegend title="Transfer status" items={LEGEND_ITEMS} />}
      />
    </>
  );
}
